import { useEffect, useState, type FormEvent } from 'react';
import { useToast } from '../ui/useToast';
import {
  useReglerRappels,
  useReglerTolerance,
  type RappelsEmargement,
  type ToleranceRetard,
} from '../../lib/useParametrage';
import {
  CLASSE_BOUTON_ENREGISTRER,
  CLASSE_CHAMP_NOMBRE,
  mentionReglage,
  messageErreur,
} from './commun';
import { SectionReglage } from './SectionReglage';

/**
 * Tolérance de retard (TF-435) : appliquée au pointage — appel, signature,
 * émargement à distance —, elle laisse « présent » qui arrive dans la fenêtre.
 * Les heures restent réelles.
 */
export function OngletEmargement({
  tolerance,
  rappels,
}: {
  tolerance: ToleranceRetard;
  rappels: RappelsEmargement;
}): JSX.Element {
  return (
    <div className="space-y-4">
      <SectionTolerance tolerance={tolerance} />
      <SectionRappels rappels={rappels} />
    </div>
  );
}

function SectionTolerance({ tolerance }: { tolerance: ToleranceRetard }): JSX.Element {
  const toast = useToast();
  const regler = useReglerTolerance();
  const [saisie, setSaisie] = useState(String(tolerance.minutes));
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => setSaisie(String(tolerance.minutes)), [tolerance.minutes]);

  const valeur = Number(saisie);
  const inchange = saisie.trim() === '' || valeur === tolerance.minutes;
  const exemple = Math.max(tolerance.minutes, 5);

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(valeur, {
      onSuccess: (nouvelle) => toast(`Tolérance de retard : ${nouvelle.texte.toLowerCase()}`),
      onError: (refus) => setErreur(messageErreur(refus, 'La tolérance n’a pas pu être enregistrée.')),
    });
  };

  return (
    <SectionReglage
      titre="Tolérance de retard"
      saisie={
        <form onSubmit={enregistrer} className="flex flex-col gap-2 sm:items-end">
          <div className="flex items-center gap-2">
            <label htmlFor="parametrage-tolerance" className="sr-only">
              Tolérance de retard en minutes
            </label>
            <input
              id="parametrage-tolerance"
              type="number"
              inputMode="numeric"
              min={0}
              max={tolerance.max}
              step={1}
              required
              value={saisie}
              onChange={(evenement) => setSaisie(evenement.target.value)}
              className={CLASSE_CHAMP_NOMBRE}
            />
            <span className="text-sm text-codialis-gray">minutes</span>
            <button type="submit" disabled={inchange || regler.isPending} className={CLASSE_BOUTON_ENREGISTRER}>
              {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
          {erreur && (
            <p role="alert" className="text-xs font-medium text-red-600">
              {erreur}
            </p>
          )}
        </form>
      }
    >
      <p className="text-xs text-codialis-gray">
        Une arrivée dans les minutes qui suivent le début de la demi-journée n’est pas un retard : la ligne reste
        « Présent ». Les heures attestées restent réelles — arrivé {exemple} minutes après le début, l’apprenant a{' '}
        {exemple} minutes de moins au certificat, au BPF et sur les factures aux heures. La tolérance s’applique au
        pointage, en salle comme à distance ; une feuille déjà pointée garde ses retards. De 0 à {tolerance.max} minutes.
      </p>
      <p className="text-[11px] font-semibold text-codialis-navy">En vigueur : {tolerance.texte}.</p>
      <p className="text-[11px] text-codialis-gray">{mentionReglage(tolerance)}</p>
    </SectionReglage>
  );
}

const CLASSE_CHAMP_HEURE =
  'min-h-[44px] w-32 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

/**
 * Rappels d'émargement (TF-460) : à ces heures, l'apprenant attendu sur la
 * feuille du jour qui n'a pas encore émargé reçoit un rappel. Une session peut
 * avoir les siennes, réglées dans « Sessions ».
 */
function SectionRappels({ rappels }: { rappels: RappelsEmargement }): JSX.Element {
  const toast = useToast();
  const regler = useReglerRappels();
  const [matin, setMatin] = useState(rappels.heureMatin);
  const [apresMidi, setApresMidi] = useState(rappels.heureApresMidi);
  const [sms, setSms] = useState(rappels.sms);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    setMatin(rappels.heureMatin);
    setApresMidi(rappels.heureApresMidi);
    setSms(rappels.sms);
  }, [rappels.heureMatin, rappels.heureApresMidi, rappels.sms]);

  const inchange = matin === rappels.heureMatin && apresMidi === rappels.heureApresMidi && sms === rappels.sms;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(
      { heureMatin: matin, heureApresMidi: apresMidi, sms },
      {
        onSuccess: (nouveaux) =>
          toast(`Rappels d’émargement à ${nouveaux.heureMatin} et ${nouveaux.heureApresMidi}`),
        onError: (refus) => setErreur(messageErreur(refus, 'Les rappels n’ont pas pu être enregistrés.')),
      },
    );
  };

  return (
    <SectionReglage
      titre="Rappels d’émargement"
      saisie={
        <form onSubmit={enregistrer} className="flex flex-col gap-2 sm:items-end">
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <label className="flex items-center gap-2 text-sm text-codialis-gray">
              Matin
              <input
                type="time"
                aria-label="Heure du rappel du matin"
                required
                min={rappels.plages.MATIN.min}
                max={rappels.plages.MATIN.max}
                value={matin}
                onChange={(evenement) => setMatin(evenement.target.value)}
                className={CLASSE_CHAMP_HEURE}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-codialis-gray">
              Après-midi
              <input
                type="time"
                aria-label="Heure du rappel de l’après-midi"
                required
                min={rappels.plages.APRES_MIDI.min}
                max={rappels.plages.APRES_MIDI.max}
                value={apresMidi}
                onChange={(evenement) => setApresMidi(evenement.target.value)}
                className={CLASSE_CHAMP_HEURE}
              />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-codialis-navy">
              <input
                type="checkbox"
                checked={sms}
                onChange={(evenement) => setSms(evenement.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Envoyer aussi un SMS
            </label>
            <button type="submit" disabled={inchange || regler.isPending} className={CLASSE_BOUTON_ENREGISTRER}>
              {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
          {erreur && (
            <p role="alert" className="text-xs font-medium text-red-600">
              {erreur}
            </p>
          )}
        </form>
      }
    >
      <p className="text-xs text-codialis-gray">
        À ces heures, chaque apprenant attendu sur la feuille du jour qui n’a pas encore émargé — ni absent justifié —
        reçoit un rappel par e-mail et, s’il les a activées, en notification sur son téléphone. Rien ne part sans
        feuille ouverte, avant le début de la demi-journée ni après sa fin ; une feuille ouverte plus tard reçoit son
        rappel à l’ouverture. Une session peut avoir ses propres heures, ou aucun rappel, dans « Sessions ».
      </p>
      <p className="text-[11px] text-codialis-gray">
        {rappels.smsRaccorde
          ? 'Chaque SMS est décompté des crédits SMS du compte d’envoi.'
          : 'Le SMS n’est pas raccordé sur la plateforme : coché, il est tracé sans être envoyé.'}
        {rappels.pushRaccorde ? '' : ' Les notifications sur téléphone ne sont pas encore raccordées.'}
      </p>
      <p className="text-[11px] font-semibold text-codialis-navy">
        En vigueur : {rappels.heureMatin} et {rappels.heureApresMidi}, {rappels.sms ? 'avec SMS' : 'sans SMS'}.
      </p>
      <p className="text-[11px] text-codialis-gray">{mentionReglage(rappels)}</p>
    </SectionReglage>
  );
}
